import { useEffect, useState } from 'preact/hooks';
import { listKnotNotes, type RecordRow } from '../lib/api';
import { comingUpDayLabel } from '../lib/calendarItems';
import { useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './spread.css';
import './householdNotes.css';

// HouseholdNotes.tsx — B17 §2: More → Notes, WHAT THE KNOT KEPT.
//
// A Knot note is an `aa_source` with `source_note: 'Knot capture (inbox)'`
// (`householdMailRoutes.ts`'s own list filters it OUT — it is not a
// message). Once a household approved one, nothing anywhere listed it again
// — the words were retained and invisible in the same act. This is that
// list: newest first, the words, the day each was kept.
//
// ⚑ A ROW, NOT TODAY'S OWN LINE (Atlas's pick, B17 §2). A kept note is not a
// thing waiting on a decision — it already was one — so it belongs beside
// Mail under More, not competing with "Needs you" for the same attention a
// pending proposal earns.
//
// NOT IN THIS PASS: linking a note to a project, editing, deleting — see
// this file's own report for why those stay follow-ups.

export interface HouseholdNotesProps {
  tenantId: string;
}

type LoadState = 'loading' | 'ready' | 'error';

export function HouseholdNotes({ tenantId }: HouseholdNotesProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [notes, setNotes] = useState<RecordRow[]>([]);
  /** Full on tap — a row starts clamped to two lines (`notes__words`) and
   * this flips ONE row past that. Keyed by id, not a single "which one",
   * so opening one never re-clamps another the reader already opened. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState('loading');
      setFailure(null);
      try {
        const rows = await listKnotNotes(tenantId);
        if (cancelled) return;
        setNotes(rows);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion]);

  return (
    <div class="sp">
      <div class="sp__eyebrow">Notes</div>

      {state === 'loading' ? <div class="sp__empty">One moment…</div> : null}

      {state === 'error' ? (
        <div class="sp__empty" data-testid="notes-failure">
          {failure?.headline}
          <br />
          <span class="sp__say-soft">{failure?.detail}</span>
        </div>
      ) : null}

      {state === 'ready' && notes.length === 0 ? (
        // AN EMPTY LIST HERE IS AN ORDINARY DAY, not a failure and not an
        // onboarding prompt — same posture as Today's own "Nothing needs
        // you right now."
        <div class="sp__empty" data-testid="notes-empty">
          Nothing kept yet.
        </div>
      ) : null}

      {state === 'ready'
        ? notes.map((n) => {
            const words = typeof n.fields['raw_text'] === 'string' ? n.fields['raw_text'] : '';
            const isOpen = Boolean(expanded[n.id]);
            return (
              <button
                type="button"
                class="sp__row"
                key={n.id}
                data-testid="notes-row"
                onClick={() => setExpanded((cur) => ({ ...cur, [n.id]: !cur[n.id] }))}
              >
                <span class={`sp__row-name notes__words${isOpen ? ' notes__words--full' : ''}`}>{words}</span>
                <span class="sp__row-sub">{comingUpDayLabel(n.created_at.slice(0, 10))}</span>
              </button>
            );
          })
        : null}
    </div>
  );
}
